import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Clock, Zap } from "lucide-react";

export function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-wine-gradient opacity-[0.03]" />
      
      {/* Decorative circles */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-wine/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left content */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-wine/10 text-wine text-sm font-medium mb-6">
                <Sparkles size={16} />
                Nova plataforma de ingressos
              </span>
            </motion.div>

            <h1
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
            >
              Venda ingressos de forma{" "}
              <span className="text-wine-gradient">fácil, rápida</span>
              {" "}e sem complicação
            </h1>

            <p
              className="text-lg text-muted-foreground leading-relaxed max-w-xl"
            >
              Com tarifas de até <strong className="text-foreground">8%</strong> e recebimento em até{" "}
              <strong className="text-foreground">2 dias úteis</strong> após o evento, 
              a Plateia.fun é a escolha certa para produtores que querem resultados.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/criar-conta">
                <Button variant="hero" size="xl" className="group w-full sm:w-auto">
                  Comece agora
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
              </Link>
              <Link to="/como-funciona">
                <Button variant="wineOutline" size="xl" className="w-full sm:w-auto">
                  Como funciona
                </Button>
              </Link>
            </motion.div>

            {/* Quick stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-8 pt-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-wine/10 flex items-center justify-center">
                  <Zap size={20} className="text-wine" />
                </div>
                <div>
                  <p className="font-heading font-semibold">Até 8%</p>
                  <p className="text-sm text-muted-foreground">de taxa</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-wine/10 flex items-center justify-center">
                  <Clock size={20} className="text-wine" />
                </div>
                <div>
                  <p className="font-heading font-semibold">2 dias</p>
                  <p className="text-sm text-muted-foreground">para receber</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right content - Illustration/Preview */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Main card */}
              <div className="bg-card rounded-2xl shadow-elevated p-6 border border-border">
                <div className="flex items-center justify-between mb-6">
                  <p className="font-heading font-semibold">Show de Verão 2025</p>
                  <span className="badge-published">Publicado</span>
                </div>
                
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="w-24 h-24 rounded-xl bg-wine-gradient flex items-center justify-center text-white">
                      <div className="text-center">
                        <p className="text-2xl font-bold">28</p>
                        <p className="text-xs uppercase">MAR</p>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-muted-foreground text-sm">Local</p>
                      <p className="font-medium">Arena São Paulo</p>
                      <p className="text-muted-foreground text-sm mt-2">Ingressos</p>
                      <p className="font-medium">1.240 vendidos</p>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Vendas</span>
                      <span className="font-medium">62%</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full w-[62%] bg-wine-gradient rounded-full" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating card */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -right-4 -bottom-8 bg-card rounded-xl shadow-card p-4 border border-border"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <span className="text-green-600 text-lg">✓</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">+R$ 2.450</p>
                    <p className="text-xs text-muted-foreground">Recebido hoje</p>
                  </div>
                </div>
              </motion.div>

              {/* Stats card */}
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                className="absolute -left-8 top-20 bg-card rounded-xl shadow-card p-4 border border-border"
              >
                <p className="text-xs text-muted-foreground">Receita total</p>
                <p className="font-heading font-bold text-xl text-wine">R$ 124.580</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom quote */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center"
      >
        <p className="text-quote text-lg text-muted-foreground italic">
          "Impossível não se divertir aqui"
        </p>
      </motion.div>
    </section>
  );
}
